import { useUnitDistances, sortByDistance, DistanceBadge } from "@/components/gps/useUnitDistances";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, formatDuration, getSignalColor, shouldFlashOrange, localHMS, formatAddressShort, composeAddress, STADIA_KEY } from "./utils";
import { LocationMap } from "./LocationMap";

// Module-scoped cache for Stadia /autocomplete responses (key = lowercased query).
// Shared across re-renders so repeated/backspaced strings never re-hit Stadia.
const AUTOCOMPLETE_CACHE = new Map();

export function CADEntry({
  callType, setCallType, priority, setPriority,
  location, setLocation, apt, setApt, phone, setPhone,
  callerSource, setCallerSource, assignedUnit, setAssignedUnit,
  locationDataInput, setLocationDataInput, locationDataList, setLocationDataList,
  cidsDataInput, setCidsDataInput, cidsDataList, setCidsDataList,
  serviceType, setServiceType,
  box, setBox,
  callInfoInput, setCallInfoInput, callInfoList, setCallInfoList,
  cadNumber, fireJobNumber, submitting, handleSubmit, editingJobId,
  units, jobs, dispatchAgencies, callTypes, callerSources,
  confirmedLocation, setConfirmedLocation, locationCoords, setLocationCoords,
  gpsPositions, callTypesDB, fireCallTypesDB,
}) {
  const isFire = (serviceType || "EMS").toUpperCase() === "FIRE";
  // Active call-type catalog flips between EMS and Fire collections.
  const activeCallTypesDB = isFire ? (fireCallTypesDB || []) : (callTypesDB || []);

  // ─── BOX auto-resolve on FIRE jobs ───
  // Iter 80 — track in-progress fetch so we can show a "fetching…" hint
  // in the BOX field instead of leaving the dispatcher staring at an
  // empty input while the geocode + closest-box chain completes.
  const [boxFetching, setBoxFetching] = useState(false);
  // When the dispatcher confirms an address (locationCoords gets set) AND
  // service_type is FIRE, hit /api/fire-boxes/closest to populate BOX.
  // The dispatcher can still edit the field manually after.
  useEffect(() => {
    if (!isFire) return undefined;
    if (!locationCoords || !Array.isArray(locationCoords) || locationCoords.length !== 2) return undefined;
    let cancelled = false;
    setBoxFetching(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/fire-boxes/closest`, {
          params: { lat: locationCoords[0], lng: locationCoords[1] },
        });
        if (!cancelled && r.data?.box_id && setBox) {
          // Only auto-fill when BOX is currently empty so manual edits stick.
          setBox((prev) => prev || r.data.box_id);
        }
      } catch {
        // Silent — BOX field stays manual-editable on failure.
      } finally {
        if (!cancelled) setBoxFetching(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isFire, locationCoords, setBox]);
  const AVAILABLE_SIGNALS = [97, 98, 99, 89, 0];
  const availableUnits = units.filter((u) => u.logged_in && AVAILABLE_SIGNALS.includes(u.signal ?? 97) && dispatchAgencies.includes(u.agency));
  const activeJobs = jobs.filter((j) => {
    if (!j.assigned_unit_id) return true;
    const unit = units.find((u) => u.id === j.assigned_unit_id);
    return unit && unit.logged_in && !AVAILABLE_SIGNALS.includes(unit.signal || 97);
  });

  // ─── Address autocomplete (Stadia /autocomplete typeahead) ───
  // Cost-saving layers:
  //   1. Early-exit if query matches the already-confirmed location (user picked one).
  //   2. Module cache by normalized query — identical substrings never re-fire.
  //   3. 500ms debounce (was 300) — waits for user to pause typing.
  //   4. AbortController — cancels in-flight Stadia requests when user keeps typing.
  const STADIA_KEY = process.env.REACT_APP_STADIA_API_KEY;
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [addrSuggestOpen, setAddrSuggestOpen] = useState(false);
  // Quick-search filter for the call-type dropdown. Local to this entry form.
  const [callTypeSearch, setCallTypeSearch] = useState("");
  // Open state for the call-type combobox dropdown. Distinct from the
  // search text so the list can also open on focus/click without
  // requiring the user to type anything.
  const [callTypeDropdownOpen, setCallTypeDropdownOpen] = useState(false);
  // Keep the search text in sync with the bound call type (e.g. when the
  // form is reset after submit, or when CADEntry is re-mounted with a
  // pre-filled callType from URL state).
  useEffect(() => { setCallTypeSearch(callType || ""); }, [callType]);
  const abortRef = useRef(null);
  useEffect(() => {
    if (!location || location.trim().length < 3) { setAddrSuggestions([]); return; }
    // If the current value already matches the confirmedLocation (user picked one), don't re-query.
    if (location === confirmedLocation) return;
    const key = location.trim().toLowerCase();
    // Cache hit → serve suggestions without touching Stadia.
    if (AUTOCOMPLETE_CACHE.has(key)) {
      setAddrSuggestions(AUTOCOMPLETE_CACHE.get(key));
      return;
    }
    const timer = setTimeout(() => {
      // Cancel any previous in-flight request (user typed more / backspaced).
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const params = new URLSearchParams({
        text: location,
        "focus.point.lat": "40.6782",
        "focus.point.lon": "-73.9442",
        "boundary.rect.min_lat": "40.49",
        "boundary.rect.min_lon": "-74.27",
        "boundary.rect.max_lat": "40.92",
        "boundary.rect.max_lon": "-73.68",
        layers: "address,street,venue",
        api_key: STADIA_KEY,
      });
      fetch(`https://api.stadiamaps.com/geocoding/v1/autocomplete?${params.toString()}`, { signal: controller.signal })
        .then((r) => r.json())
        .then((d) => {
          const feats = (d.features || []).slice(0, 7).map((f) => ({
            id: f.properties?.gid || f.properties?.id,
            label: f.properties?.label || "",
            lng: f.geometry?.coordinates?.[0],
            lat: f.geometry?.coordinates?.[1],
          })).filter(x => x.label);
          AUTOCOMPLETE_CACHE.set(key, feats);
          setAddrSuggestions(feats);
        }).catch(() => setAddrSuggestions([]));
    }, 500);
    return () => clearTimeout(timer);
  }, [location, confirmedLocation, STADIA_KEY]);

  const addLocationData = () => {
    if (!locationDataInput.trim()) return;
    setLocationDataList((p) => [...p, `${localHMS()} - ${locationDataInput.trim()}`]);
    setLocationDataInput("");
  };

  const addCidsData = () => {
    if (!cidsDataInput.trim()) return;
    setCidsDataList((p) => [...p, `${localHMS()} - ${cidsDataInput.trim()}`]);
    setCidsDataInput("");
  };

  const addCallInfo = () => {
    if (!callInfoInput.trim()) return;
    setCallInfoList((p) => [...p, `${localHMS()} - ${callInfoInput.trim()}`]);
    setCallInfoInput("");
  };

  // Iter 94 — Verified Address indicator state. After a successful
  // backend geocode resolve, we show a green check next to the Location
  // input with the resolved borough on hover. A red/amber state appears
  // when the address can't be verified inside NYC so the dispatcher
  // knows the marker may be wrong before transmitting.
  const [addrVerified, setAddrVerified] = useState(null);
  // null = not yet attempted, true = verified, false = lookup failed.
  const [addrVerifiedMsg, setAddrVerifiedMsg] = useState("");
  // Reset verification whenever the user edits the Location text away
  // from the last confirmed value.
  useEffect(() => {
    if (location !== confirmedLocation) {
      setAddrVerified(null);
      setAddrVerifiedMsg("");
    }
  }, [location, confirmedLocation]);

  const confirmLocation = async () => {
    const trimmed = location.trim();
    if (!trimmed) return;
    setConfirmedLocation(trimmed);
    // Fetch saved location notes (existing behavior).
    axios.get(`${API}/location-notes?address=${encodeURIComponent(trimmed)}`)
      .then((r) => {
        if (r.data.notes && r.data.notes.length > 0) {
          setLocationDataList((prev) => {
            const existing = new Set(prev);
            const newNotes = r.data.notes.filter((n) => !existing.has(n));
            return [...prev, ...newNotes];
          });
        }
      }).catch(() => {});

    // Iter 94 — Always use the backend's house-number + borough validated
    // geocoder for the marker coords (NOT raw Stadia first-result). This
    // is the same validation the /api/jobs POST uses, so the map marker
    // and the persisted job coords match — fixing the prior "wrong
    // building 2km away" bug at the FRONTEND layer too. Returns
    // `verified=false` for ambiguous / out-of-borough inputs; we surface
    // that as a red ✗ badge so the dispatcher corrects the address
    // before TRANSMITting the call.
    let coords = null;
    try {
      const vr = await axios.get(`${API}/geocode-address`, { params: { address: trimmed } });
      if (vr.data?.verified && vr.data?.lat != null && vr.data?.lng != null) {
        coords = [vr.data.lat, vr.data.lng];
        setLocationCoords(coords);
        setAddrVerified(true);
        setAddrVerifiedMsg(`✓ Verified${vr.data.borough ? ` in ${vr.data.borough}` : ""} — ${vr.data.lat.toFixed(5)}, ${vr.data.lng.toFixed(5)}`);
      } else {
        setAddrVerified(false);
        setAddrVerifiedMsg(vr.data?.message || "Address could not be verified inside NYC.");
      }
    } catch {
      setAddrVerified(false);
      setAddrVerifiedMsg("Verification service unavailable.");
    }
    // Direct BOX fetch on FIRE — don't wait for the auto-fill effect's
    // dependency cycle to settle. Only overrides empty BOX so manual
    // entries stick.
    if (isFire && coords && Array.isArray(coords) && coords.length === 2) {
      setBoxFetching(true);
      try {
        const r = await axios.get(`${API}/fire-boxes/closest`, {
          params: { lat: coords[0], lng: coords[1] },
        });
        if (r.data?.box_id && setBox) {
          setBox((prev) => prev || r.data.box_id);
        }
      } catch { /* silent — BOX field stays manual-editable on failure */ }
      finally { setBoxFetching(false); }
    }
  };

  const unitDist = useUnitDistances(locationCoords?.[0], locationCoords?.[1], gpsPositions);
  const sortedAvailableUnits = sortByDistance(availableUnits, unitDist);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", overflow: "hidden" }}>
        {/* LEFT COLUMN */}
        <div style={{ display: "flex", flexDirection: "column", borderRight: "1px solid #333", overflow: "auto" }}>
          {/* Form */}
          <div style={{ padding: 12 }}>
            <div className="dispatch-form-row" data-testid="cad-service-type-row">
              <label>Service</label>
              <select
                data-testid="cad-service-type"
                className="dispatch-select"
                value={serviceType || "EMS"}
                onChange={(e) => {
                  setServiceType(e.target.value);
                  // Clear the call-type when toggling so the dispatcher
                  // doesn't accidentally save an EMS call-type on a Fire
                  // job (or vice versa).
                  setCallType("");
                }}
                style={{
                  fontWeight: 700,
                  background: (serviceType || "EMS").toUpperCase() === "FIRE" ? "#FFE0CC" : "#E0E8F0",
                  color: "#000",
                }}
              >
                <option value="EMS">EMS</option>
                <option value="FIRE">FIRE</option>
              </select>
              <span style={{ marginLeft: 12, fontSize: 11, color: "#888", fontStyle: "italic" }}>
                {(serviceType || "EMS").toUpperCase() === "FIRE"
                  ? "Fire CAD entry — call-types and CIDS data swap in below."
                  : "EMS CAD entry — Location Data section is shown."}
              </span>
            </div>
            <div className="dispatch-form-row" style={{ position: "relative" }}>
              <label>Call Type</label>
              {(() => {
                const source = activeCallTypesDB.length > 0 ? activeCallTypesDB.map((t) => t.call_type) : callTypes;
                const q = callTypeSearch.trim().toLowerCase();
                const list = [...source].filter(Boolean).sort((a, b) => a.localeCompare(b));
                // Filter only when the user is actively typing something
                // distinct from the bound call type. Clicking the field
                // (or pressing the chevron) opens the FULL list so the
                // user can browse without typing.
                const isSearching = q && callTypeSearch !== callType;
                const filtered = isSearching ? list.filter((t) => t.toLowerCase().includes(q)) : list;
                const showDropdown = callTypeDropdownOpen && filtered.length > 0;
                return (
                  <>
                    <input
                      data-testid="cad-call-type"
                      className="dispatch-input"
                      type="text"
                      placeholder="Click or type to search…"
                      value={callTypeSearch}
                      onChange={(e) => {
                        setCallTypeSearch(e.target.value);
                        setCallTypeDropdownOpen(true);
                        // Clear the bound call_type if the user is editing the search.
                        if (e.target.value !== callType) setCallType("");
                      }}
                      onFocus={() => setCallTypeDropdownOpen(true)}
                      onClick={() => setCallTypeDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setCallTypeDropdownOpen(false), 150)}
                      style={{ flex: 1, color: "#000" }}
                      autoComplete="off"
                    />
                    {/* Chevron toggle button — click to open/close the dropdown
                        without changing focus. Mirrors the native <select>
                        affordance so the input doubles as a dropdown. */}
                    <button
                      type="button"
                      data-testid="cad-call-type-toggle"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setCallTypeDropdownOpen((v) => !v);
                      }}
                      style={{
                        background: "#FFF", border: "1px solid #999", borderLeft: "none",
                        padding: "0 8px", cursor: "pointer", color: "#000",
                        fontSize: 12, height: 26,
                      }}
                      tabIndex={-1}
                      aria-label="Toggle call type list"
                    >
                      ▼
                    </button>
                    {showDropdown && (
                      <div data-testid="cad-call-type-dropdown" style={{
                        position: "absolute", top: "100%", left: 80, right: 76, zIndex: 30,
                        background: "#FFF", border: "1px solid #888", maxHeight: 220, overflowY: "auto",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                      }}>
                        {filtered.slice(0, 200).map((t) => (
                          <div key={t}
                            data-testid={`cad-call-type-opt-${t}`}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setCallType(t);
                              setCallTypeSearch(t);
                              setCallTypeDropdownOpen(false);
                              const ct = activeCallTypesDB.find((c) => c.call_type === t);
                              if (ct && ct.segment) setPriority(ct.segment);
                            }}
                            style={{ padding: "4px 8px", fontSize: 12, color: "#000", cursor: "pointer", borderBottom: "1px solid #EEE" }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "#DDEEFF")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                            {t}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
              <label style={{ marginLeft: 8 }}>Segment</label>
              <input
                data-testid="cad-priority"
                className="dispatch-input"
                type="text"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                style={{ width: 60, flex: "none" }}
                readOnly={!!activeCallTypesDB.find((c) => c.call_type === callType)?.segment}
              />
            </div>

            <div className="dispatch-form-row" style={{ position: "relative" }}>
              <label>Location</label>
              <input
                data-testid="cad-location"
                className="dispatch-input"
                type="text"
                placeholder="Start typing an address..."
                value={location}
                onChange={(e) => { setLocation(e.target.value); setAddrSuggestOpen(true); }}
                onFocus={() => setAddrSuggestOpen(true)}
                onBlur={() => setTimeout(() => setAddrSuggestOpen(false), 200)}
                onKeyDown={(e) => { if (e.key === "Enter") confirmLocation(); }}
                autoComplete="off"
              />
              <button
                data-testid="cad-confirm-location"
                onClick={confirmLocation}
                style={{ background: "#00BFFF", color: "#FFF", border: "none", padding: "5px 12px", fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}
              >
                Confirm
              </button>
              {/* Iter 94 — Verified address badge. Green check when the
                  backend geocoder validated house number + borough; red
                  cross when it couldn't (ambiguous or out-of-NYC). */}
              {addrVerified === true && (
                <span
                  data-testid="cad-addr-verified"
                  title={addrVerifiedMsg}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                    background: "#0c4a25",
                    color: "#7CFFA9",
                    border: "1px solid #1f8a4d",
                    borderRadius: 3,
                    padding: "3px 8px",
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 700,
                    fontSize: 11,
                    whiteSpace: "nowrap",
                    cursor: "help",
                  }}
                >
                  ✓ Verified
                </span>
              )}
              {addrVerified === false && (
                <span
                  data-testid="cad-addr-unverified"
                  title={addrVerifiedMsg}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                    background: "#5a1a1a",
                    color: "#FF9C9C",
                    border: "1px solid #c44",
                    borderRadius: 3,
                    padding: "3px 8px",
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 700,
                    fontSize: 11,
                    whiteSpace: "nowrap",
                    cursor: "help",
                  }}
                >
                  ✗ Not verified
                </span>
              )}
              {/* Autocomplete dropdown — typeahead powered by Stadia /autocomplete. */}
              {addrSuggestOpen && addrSuggestions.length > 0 && (
                <div style={{ position: "absolute", top: "100%", left: 100, right: 70, background: "#FFF", border: "1px solid #BBB", borderRadius: 3, maxHeight: 220, overflowY: "auto", zIndex: 50, boxShadow: "0 3px 10px rgba(0,0,0,0.25)" }}>
                  {addrSuggestions.map((s, i) => (
                    <div
                      key={s.id || i}
                      data-testid={`addr-suggest-${i}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        const chosen = s.label;
                        setLocation(chosen);
                        setConfirmedLocation(chosen);
                        if (s.lat && s.lng) setLocationCoords([s.lat, s.lng]);
                        setAddrSuggestOpen(false);
                        setAddrSuggestions([]);
                      }}
                      style={{ padding: "6px 10px", cursor: "pointer", fontSize: 12, color: "#000", borderBottom: "1px solid #EEE", fontFamily: "'Manrope', sans-serif" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#E8F1FF")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "#FFF")}
                    >
                      {s.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="dispatch-form-row">
              <label>Apt.</label>
              <input
                data-testid="cad-apt"
                className="dispatch-input"
                type="text"
                value={apt}
                onChange={(e) => setApt(e.target.value)}
                style={{ width: 60, flex: "none" }}
              />
              <label>Phone</label>
              <input
                data-testid="cad-phone"
                className="dispatch-input"
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            <div className="dispatch-form-row">
              <label>Caller Source</label>
              <select
                data-testid="cad-caller-source"
                className="dispatch-select"
                value={callerSource}
                onChange={(e) => setCallerSource(e.target.value)}
              >
                <option value="">Select...</option>
                {callerSources.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* FDNY alarm-box — Fire-only field, auto-resolves from address. */}
            {isFire && (
              <div className="dispatch-form-row" data-testid="cad-box-row">
                <label>Box</label>
                <input
                  data-testid="cad-box"
                  className="dispatch-input"
                  type="text"
                  value={box || ""}
                  onChange={(e) => setBox(e.target.value.toUpperCase())}
                  placeholder={boxFetching ? "Resolving box from address…" : "Auto-fills from address (e.g. B0370)"}
                  style={{ width: 180, flex: "none", fontFamily: "monospace", fontWeight: 700, background: boxFetching ? "#FFF0C0" : "#FFF8E0" }}
                />
                <span style={{ fontSize: 11, color: boxFetching ? "#A06A00" : "#888", fontStyle: "italic", marginLeft: 8 }} data-testid="cad-box-hint">
                  {boxFetching ? "Resolving closest FDNY alarm box…" : "Closest FDNY alarm box. Override if needed."}
                </span>
              </div>
            )}
          </div>

          {/* LOCATION DATA / CIDS DATA — swaps based on EMS vs Fire service type. */}
          {!isFire ? (
            <div className="section-box" style={{ flex: 1 }} data-testid="location-data-section">
              <div className="section-header">LOCATION DATA</div>
              <div className="section-content" style={{ overflow: "auto" }}>
                {locationDataList.map((item, i) => (
                  <div key={i} style={{ padding: "2px 0", fontSize: 13 }}>{item}</div>
                ))}
              </div>
              <div className="add-row">
                <input
                  data-testid="location-data-input"
                  value={locationDataInput}
                  onChange={(e) => setLocationDataInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addLocationData()}
                  placeholder="Add location note..."
                />
                <button data-testid="location-data-add" className="add-btn" onClick={addLocationData}>Add</button>
              </div>
            </div>
          ) : (
            <div className="section-box" style={{ flex: 1, borderColor: "#C09060" }} data-testid="cids-data-section">
              <div className="section-header" style={{ background: "#FFE0CC", color: "#7A3A00" }}>CIDS DATA</div>
              <div className="section-content" style={{ overflow: "auto" }}>
                {(cidsDataList || []).map((item, i) => (
                  <div key={i} style={{ padding: "2px 0", fontSize: 13 }}>{item}</div>
                ))}
              </div>
              <div className="add-row">
                <input
                  data-testid="cids-data-input"
                  value={cidsDataInput || ""}
                  onChange={(e) => setCidsDataInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addCidsData()}
                  placeholder="Hazards, hydrant, owner contact..."
                />
                <button data-testid="cids-data-add" className="add-btn" onClick={addCidsData}>Add</button>
              </div>
            </div>
          )}

          {/* CALL INFO */}
          <div className="section-box" style={{ flex: 1 }}>
            <div className="section-header">CALL INFO</div>
            <div className="section-content" style={{ overflow: "auto" }}>
              {callInfoList.map((item, i) => (
                <div key={i} style={{ padding: "2px 0", fontSize: 13 }}>{item}</div>
              ))}
            </div>
            <div className="add-row">
              <input
                data-testid="call-info-input"
                value={callInfoInput}
                onChange={(e) => setCallInfoInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addCallInfo()}
                placeholder="Add call note..."
              />
              <button data-testid="call-info-add" className="add-btn" onClick={addCallInfo}>Add</button>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* LOCATION / Map */}
          <div className="section-box" style={{ flex: 2 }}>
            <div className="section-header">LOCATION</div>
            <div style={{ flex: 1, position: "relative", minHeight: 200 }}>
              <LocationMap
                location={confirmedLocation}
                apt={apt}
                preResolvedCoords={locationCoords}
                onCoordsChange={setLocationCoords}
                onResolvedAddress={(addr) => { if (addr && addr !== confirmedLocation) { setLocation(addr); setConfirmedLocation(addr); } }}
              />
            </div>
          </div>

          {/* AVAILABLE UNITS - Click to assign */}
          <div className="section-box" style={{ flex: 1 }}>
            <div className="section-header">AVAILABLE UNITS — CLICK TO ASSIGN</div>
            <div className="section-content" style={{ padding: 0, overflow: "auto" }}>
              {sortedAvailableUnits.length === 0 ? (
                <div style={{ padding: 8, color: "#888", fontSize: 13 }}>No available units</div>
              ) : (
                sortedAvailableUnits.map((u, i) => {
                  return (
                    <div
                      key={u.id}
                      className="unit-row"
                      data-testid={`available-unit-${u.name}`}
                      onClick={() => setAssignedUnit(assignedUnit === u.id ? "" : u.id)}
                      style={{
                        background: assignedUnit === u.id ? "#003355" : undefined,
                        borderLeft: assignedUnit === u.id ? "3px solid #00BFFF" : "3px solid transparent",
                        cursor: "pointer",
                      }}
                    >
                      <span style={{ marginRight: 8, color: "#888" }}>{i + 1}.</span>
                      <span className="unit-name" style={{ color: assignedUnit === u.id ? "#00BFFF" : "#FFF" }}>{u.name}</span>
                      <span style={{ marginLeft: 8 }}><DistanceBadge d={unitDist[u.id]} dark testId={`available-unit-dist-${u.name}`} /></span>
                      <span style={{ flex: 1 }} />
                      {assignedUnit === u.id && (
                        <span style={{ color: "#00BFFF", fontSize: 11, fontWeight: 700, fontFamily: "'Manrope', sans-serif", textTransform: "uppercase" }}>ASSIGNED</span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* ACTIVE UNITS */}
          <div className="section-box" style={{ flex: 1 }}>
            <div className="section-header">ACTIVE UNITS</div>
            <div className="section-content" style={{ padding: 0, overflow: "auto" }}>
              {activeJobs.length === 0 ? (
                <div style={{ padding: 8, color: "#888", fontSize: 13 }}>No active units</div>
              ) : (
                activeJobs.map((j) => {
                  const unit = units.find((u) => u.id === j.assigned_unit_id);
                  const isPending = !j.assigned_unit_id;
                  const ctMatch = callTypesDB.find((c) => c.call_type === j.call_type);
                  const fireCtMatch = (fireCallTypesDB || []).find((c) => c.call_type === j.call_type);
                  const jobIsFire = (j.service_type || "EMS").toUpperCase() === "FIRE";
                  const callCode = jobIsFire
                    ? (fireCtMatch ? fireCtMatch.call_code : j.call_type)
                    : (ctMatch ? ctMatch.call_code : j.call_type);
                  const jobNumber = jobIsFire ? (j.fire_job_number || j.cad_number) : j.cad_number;
                  const pendingFlash = isPending && (new Date() - new Date(j.created_at) > 120000);
                  const unitFlash = !isPending && unit && shouldFlashOrange(unit);
                  const emergencyFlash = !isPending && unit && unit.emergency_active;
                  return (
                    <div key={j.id} className="unit-row active" data-testid={`active-job-${j.cad_number}`}
                      style={{ animation: emergencyFlash ? "flashRed 0.7s infinite" : pendingFlash ? "flashYellow 1s infinite" : unitFlash ? "flashOrange 1s infinite" : undefined }}>
                      <span className="unit-name" style={{ minWidth: 80, color: isPending ? "#FFCC00" : "#FFF" }}>
                        {j.assigned_unit_name || (isPending ? "PENDING" : "—")}
                      </span>
                      <span style={{ minWidth: 40, fontWeight: 700, color: isPending ? "#FFCC00" : getSignalColor(unit?.signal), fontFamily: "'IBM Plex Mono', monospace" }}>
                        {isPending ? "---" : (unit ? (unit.signal === 0 ? "NOSTAT" : unit.signal === 10 ? "DISP" : unit.signal) : "")}
                      </span>
                      <span style={{ minWidth: 50, fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", color: jobIsFire ? "#FF8844" : undefined, fontWeight: jobIsFire ? 700 : undefined }}>{jobNumber}</span>
                      <span style={{ minWidth: 60, fontSize: 12, color: "#AAA" }}>{callCode}</span>
                      <span style={{ flex: 1, fontSize: 12 }}>
                        {(unit?.signal === 82 || unit?.signal === 81) && unit?.hospital_transport?.hospital_code
                          ? `${unit.hospital_transport.hospital_code} \\ ${formatAddressShort(composeAddress(j.location, j.apt))}`
                          : formatAddressShort(composeAddress(j.location, j.apt))}
                      </span>
                      <span style={{ minWidth: 40, textAlign: "right", color: "#AAA", fontSize: 12, fontFamily: "'IBM Plex Mono', monospace" }}>
                        {isPending ? formatDuration(j.created_at) : formatDuration(unit?.status_changed_at)}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM BAR */}
      <div className="bottom-bar">
        {isFire ? (
          <span data-testid="cad-number" style={{ display: "inline-flex", gap: 12, alignItems: "center" }}>
            <span data-testid="fire-job-number" style={{ color: "#FF8844", fontWeight: 700 }}>
              FIRE JOB #{fireJobNumber || "---"}
            </span>
            <span style={{ color: "#888" }}>CAD #{cadNumber || "---"}</span>
          </span>
        ) : (
          <span data-testid="cad-number">
            CAD #{cadNumber || "---"}
          </span>
        )}
        <button
          data-testid="cad-submit"
          className="submit-btn"
          onClick={handleSubmit}
          disabled={submitting || !callType || !location}
        >
          {submitting ? "Submitting..." : editingJobId ? "Update Call" : "Submit"}
        </button>
      </div>
    </div>
  );
}
